import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Loader2,
  Plus, Trash2,
  Workflow, Check, Clock, Play, SkipForward,
  ArrowRight, CheckCircle, Circle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import Sidebar from '../components/Sidebar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stepStatusIcon = {
  pending: <Circle className="w-4 h-4 text-muted-foreground" />,
  in_progress: <Play className="w-4 h-4 text-blue-400" />,
  completed: <CheckCircle className="w-4 h-4 text-green-400" />,
  skipped: <SkipForward className="w-4 h-4 text-gray-400" />,
};

export default function WorkflowsPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [showNewWorkflow, setShowNewWorkflow] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newWorkflow, setNewWorkflow] = useState({ name: '', template: '' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [wfRes, tmplRes] = await Promise.all([
        axios.get(`${API}/workflows`),
        axios.get(`${API}/workflows/templates`),
      ]);
      setWorkflows(wfRes.data.workflows);
      setTemplates(tmplRes.data.templates);
    } catch (error) {
      toast.error('Blad ladowania danych');
    } finally {
      setLoading(false);
    }
  };

  const createWorkflow = async () => {
    if (!newWorkflow.name) { toast.error('Wpisz nazwe'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/workflows`, {
        name: newWorkflow.name,
        template: newWorkflow.template || null,
      });
      toast.success('Workflow utworzony');
      setShowNewWorkflow(false);
      setNewWorkflow({ name: '', template: '' });
      fetchData();
    } catch (error) {
      toast.error('Blad tworzenia');
    } finally {
      setCreating(false);
    }
  };

  const updateStep = async (workflowId, stepId, status) => {
    try {
      const res = await axios.put(`${API}/workflows/${workflowId}/steps/${stepId}`, { status });
      toast.success(res.data.message);
      fetchData();
      // Refresh selected workflow
      if (selectedWorkflow?.id === workflowId) {
        const wfRes = await axios.get(`${API}/workflows/${workflowId}`);
        setSelectedWorkflow(wfRes.data);
      }
    } catch (error) {
      toast.error('Blad aktualizacji');
    }
  };

  const deleteWorkflow = async (workflowId) => {
    if (!window.confirm('Na pewno usunac workflow?')) return;
    try {
      await axios.delete(`${API}/workflows/${workflowId}`);
      toast.success('Workflow usuniety');
      if (selectedWorkflow?.id === workflowId) setSelectedWorkflow(null);
      fetchData();
    } catch (error) {
      toast.error('Blad usuwania');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/workflows" />

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-10">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="text-3xl font-bold mb-1">{language === 'pl' ? 'Automatyzacja' : 'Workflows'}</h1>
            <p className="text-muted-foreground">{language === 'pl' ? 'Automatyczne pipeline od rezerwacji do opinii' : 'Automated pipeline from booking to review'}</p>
          </div>
          <Button onClick={() => setShowNewWorkflow(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {language === 'pl' ? 'Nowy workflow' : 'New Workflow'}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Workflows List */}
          <div className="lg:col-span-1 space-y-3">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              {language === 'pl' ? 'Aktywne workflow' : 'Active Workflows'} ({workflows.length})
            </h2>
            {workflows.length === 0 ? (
              <Card>
                <CardContent className="p-6 text-center">
                  <Workflow className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">{language === 'pl' ? 'Brak workflow' : 'No workflows'}</p>
                </CardContent>
              </Card>
            ) : workflows.map((wf, i) => {
              const completedSteps = (wf.steps || []).filter(s => s.status === 'completed' || s.status === 'skipped').length;
              const totalSteps = (wf.steps || []).length;
              const progress = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

              return (
                <motion.div key={wf.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}>
                  <Card
                    className={`cursor-pointer transition-colors ${selectedWorkflow?.id === wf.id ? 'border-primary' : 'hover:border-primary/30'}`}
                    onClick={() => setSelectedWorkflow(wf)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-sm">{wf.name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${wf.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'}`}>
                          {wf.status === 'completed' ? 'Zakonczony' : 'Aktywny'}
                        </span>
                      </div>
                      <div className="w-full bg-secondary rounded-full h-1.5 mb-2">
                        <div className="bg-primary h-1.5 rounded-full transition-all" style={{ width: `${progress}%` }} />
                      </div>
                      <p className="text-xs text-muted-foreground">{completedSteps}/{totalSteps} krokow</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Workflow Detail */}
          <div className="lg:col-span-2">
            {selectedWorkflow ? (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{selectedWorkflow.name}</CardTitle>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteWorkflow(selectedWorkflow.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(selectedWorkflow.steps || []).map((step, i) => (
                      <div key={step.id} className="flex items-center gap-4 p-3 rounded-lg bg-secondary/30">
                        <div className="flex-shrink-0">{stepStatusIcon[step.status] || stepStatusIcon.pending}</div>
                        <div className="flex-1">
                          <p className="font-medium text-sm">{step.name}</p>
                          <p className="text-xs text-muted-foreground">{step.trigger}</p>
                          {step.completed_at && <p className="text-xs text-green-400 mt-1">Wykonano: {new Date(step.completed_at).toLocaleString('pl-PL')}</p>}
                        </div>
                        <div className="flex gap-1">
                          {step.status === 'pending' && (
                            <>
                              <Button size="sm" variant="outline" onClick={() => updateStep(selectedWorkflow.id, step.id, 'completed')}>
                                <Check className="w-3 h-3 mr-1" />Wykonaj
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => updateStep(selectedWorkflow.id, step.id, 'skipped')}>
                                <SkipForward className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                        </div>
                        {i < selectedWorkflow.steps.length - 1 && (
                          <ArrowRight className="w-4 h-4 text-muted-foreground absolute -bottom-3 left-6 hidden" />
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-16 text-center">
                  <Workflow className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold mb-2">{language === 'pl' ? 'Wybierz workflow' : 'Select a workflow'}</h3>
                  <p className="text-sm text-muted-foreground">{language === 'pl' ? 'Kliknij workflow z listy aby zobaczyc szczegoly' : 'Click a workflow from the list to see details'}</p>
                </CardContent>
              </Card>
            )}

            {/* Templates */}
            <div className="mt-6">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                {language === 'pl' ? 'Dostepne szablony' : 'Available Templates'}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {templates.map((tmpl) => (
                  <Card key={tmpl.id} className="hover:border-primary/30 transition-colors">
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-sm mb-1">{tmpl.name}</h3>
                      <p className="text-xs text-muted-foreground mb-3">{tmpl.steps_count || tmpl.steps?.length || 0} krokow</p>
                      <Button size="sm" variant="outline" className="w-full" onClick={() => {
                        setNewWorkflow({ name: tmpl.name, template: tmpl.id });
                        setShowNewWorkflow(true);
                      }}>
                        <Play className="w-3 h-3 mr-1" />Uzyj
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* New Workflow Dialog */}
        <Dialog open={showNewWorkflow} onOpenChange={setShowNewWorkflow}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{language === 'pl' ? 'Nowy workflow' : 'New Workflow'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nazwa</Label>
                <Input value={newWorkflow.name} onChange={e => setNewWorkflow(p => ({ ...p, name: e.target.value }))} placeholder="Np. Wesele Kowalskich" />
              </div>
              <div>
                <Label>Szablon</Label>
                <select
                  className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm"
                  value={newWorkflow.template}
                  onChange={e => setNewWorkflow(p => ({ ...p, template: e.target.value }))}
                >
                  <option value="">Pusty workflow</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name} ({t.steps_count || t.steps?.length || 0} krokow)</option>)}
                </select>
              </div>
              <Button onClick={createWorkflow} disabled={creating} className="w-full">
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Utworz workflow
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
